/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import classNames from 'classnames';

import type * as Type from '@/common/interface';
import { REACT_BASE_PATH } from '@/router/alias';

import './index.scss';

type SyncedTool = {
  title: string;
  version: string;
  description: string;
  href: string;
  downloads?: number;
};

export const AITO_FORUM_BOARDS = [
  {
    name: '公告',
    slug: 'notice',
    description: '站长公告、规则、维护通知',
  },
  {
    name: '工具',
    slug: 'tool',
    description: '工具安装、下载、反馈、插件市场',
  },
];

const newApiLogin = '/login?redirect=/forum/';

export const isAitoBoard = (slug?: string) =>
  AITO_FORUM_BOARDS.some((board) => board.slug === slug);

export const isAitoRootAdmin = (user?: Partial<Type.UserInfoRes>) =>
  user?.username === 'rootadmin' && user?.role_id === 2;

export const boardPath = (slug: string) => `/tags/${slug}/questions`;

export const askPath = (tags = '') =>
  tags ? `/questions/ask?tags=${encodeURIComponent(tags)}` : '/questions/ask';

export const AitoForumTopLinks = ({
  user,
}: {
  user?: Partial<Type.UserInfoRes>;
}) => {
  const { pathname } = useLocation();
  const isForum =
    pathname === '/' ||
    pathname.startsWith('/questions') ||
    pathname.startsWith('/tags');
  return (
    <nav className="aito-top-links" aria-label="AitoAPI forum links">
      <a href="/tool/">工具</a>
      <NavLink to="/questions" className={isForum ? 'active' : undefined}>
        论坛
      </NavLink>
      <a
        href={
          user?.username ? '/console/token' : '/login?redirect=/console/token'
        }>
        获取密钥
      </a>
      <a
        href={
          user?.username ? '/console/topup' : '/login?redirect=/console/topup'
        }>
        获取额度
      </a>
      {isAitoRootAdmin(user) ? (
        <NavLink className="admin-link" to="/admin">
          管理
        </NavLink>
      ) : null}
    </nav>
  );
};

export const AitoForumHero = ({ activeSlug }: { activeSlug?: string }) => {
  const board = AITO_FORUM_BOARDS.find((item) => item.slug === activeSlug);
  return (
    <section className="aito-forum-hero">
      <div>
        <span className="aito-kicker">AitoAPI 论坛</span>
        <h1>{board ? `${board.name}板块` : '论坛首页'}</h1>
        <p>
          复用现有登录态，公告和工具作为一级板块，具体内容继续使用关键词标签。
        </p>
      </div>
      <div className="aito-hero-actions">
        <Link className="btn btn-primary btn-sm" to={askPath(activeSlug || '')}>
          发帖
        </Link>
        <a className="btn btn-outline-secondary btn-sm" href="/tool/">
          工具页
        </a>
      </div>
    </section>
  );
};

export const AitoForumBoardNav = ({ activeSlug }: { activeSlug?: string }) => {
  return (
    <div className="aito-board-nav" aria-label="一级板块">
      {AITO_FORUM_BOARDS.map((board) => (
        <NavLink
          key={board.slug}
          to={boardPath(board.slug)}
          className={classNames('aito-board-link', {
            active: activeSlug === board.slug,
          })}>
          <span>
            <strong>{board.name}</strong>
          </span>
          <em>{board.description}</em>
        </NavLink>
      ))}
    </div>
  );
};

export const AitoForumAd = () => (
  <a
    className="aito-forum-ad"
    href="https://www.aitoapi.cloud/"
    target="_blank"
    rel="noreferrer"
    aria-label="AitoAPI Codex中转">
    <span className="ad-card ad-pain">
      <strong>token不够用怎么办？</strong>
      <strong>网络卡顿怎么办？</strong>
    </span>
    <span className="ad-card ad-offer">
      <em>Codex中转</em>
      <strong>1元 = 10$</strong>
      <span>国内畅快直连</span>
      <span>最新GPT模型</span>
      <span>官方1:1计费</span>
      <b>立即使用</b>
    </span>
    <span className="ad-card ad-cache">
      <strong>高缓存命中</strong>
      <span>纯Plus使用</span>
    </span>
    <span className="ad-card ad-model">
      <em>Gpt5.5模型</em>
      <span>1元 = 输入 400万token</span>
      <span>1元 = 输出 60万token</span>
      <span>1元 = 缓存 1亿token</span>
    </span>
  </a>
);

export const AitoForumAskGuide = () => (
  <section className="aito-ask-guide">
    <div>
      <span className="aito-kicker">一级板块标签</span>
      <strong>发帖时可选择多个板块</strong>
      <p>选择公告、工具等板块后，再添加工具名、问题类型等关键词。</p>
    </div>
    <div className="aito-board-pills">
      {AITO_FORUM_BOARDS.map((board) => (
        <Link key={board.slug} to={askPath(board.slug)}>
          {board.name}
        </Link>
      ))}
      <Link to={askPath('notice,tool')}>公告 + 工具</Link>
    </div>
  </section>
);

export const AitoForumAdminPanel = () => (
  <section className="aito-admin-panel">
    <div>
      <span className="aito-kicker">AitoAPI 论坛规则</span>
      <h4>论坛管理后台</h4>
      <p>
        内容审核、用户管理、标签管理、文件上传、评论和投票由论坛后台统一处理。
        AitoAPI 增加登录桥、一级板块和发帖权限约束。
      </p>
    </div>
    <div className="aito-admin-grid">
      <Link to="/admin/tags/settings">板块标签</Link>
      <Link to="/admin/qa/questions">内容审核</Link>
      <Link to="/admin/users">用户管理</Link>
      <Link to="/admin/rules/privileges">权限规则</Link>
    </div>
  </section>
);

export const withForumBase = (path: string) => `${REACT_BASE_PATH}${path}`;

export function AitoForumToolFeed({ compact = false }: { compact?: boolean }) {
  const [tools, setTools] = useState<SyncedTool[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    const readToolText = (element: Element | null | undefined) =>
      (element?.textContent || '').replace(/\s+/g, ' ').trim();

    const loadTools = async () => {
      try {
        const [toolHtml, stats] = await Promise.all([
          fetch('/tool/', {
            cache: 'no-store',
            credentials: 'same-origin',
          }).then((res) => res.text()),
          fetch('/tool/api/download-stats', {
            cache: 'no-store',
            credentials: 'same-origin',
          })
            .then((res) => (res.ok ? res.json() : { files: {} }))
            .catch(() => ({ files: {} })),
        ]);
        const doc = new DOMParser().parseFromString(toolHtml, 'text/html');
        const cards = Array.prototype.slice.call(
          doc.querySelectorAll('[data-content-card][data-content-type="tool"]'),
        ) as HTMLElement[];
        const nextTools = cards
          .map((card) => {
            const heading = card.querySelector('h2');
            const version = readToolText(heading?.querySelector('span'));
            const headingClone = heading?.cloneNode(true) as HTMLElement | null;
            headingClone?.querySelector('span')?.remove();
            const title = readToolText(headingClone);
            const download = card.querySelector(
              'a.primary-link',
            ) as HTMLAnchorElement | null;
            const statKey = card
              .querySelector('[data-download-count]')
              ?.getAttribute('data-download-count');
            return {
              title,
              version,
              description: readToolText(card.querySelector('p')),
              href: download?.getAttribute('href') || '/tool/',
              downloads:
                statKey && stats?.files
                  ? Number(stats.files[statKey] || 0)
                  : undefined,
            };
          })
          .filter((item) => item.title)
          .slice(0, compact ? 3 : 8);

        if (!ignore) {
          setTools(nextTools);
        }
      } catch {
        if (!ignore) {
          setTools([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadTools();
    return () => {
      ignore = true;
    };
  }, [compact]);

  return (
    <section
      className={classNames('aito-tool-feed', { compact })}
      aria-label="工具页同步内容">
      <div className="aito-tool-feed-head">
        <strong>工具同步</strong>
        <a href="/tool/">查看全部</a>
      </div>
      {loading ? (
        <p className="text-secondary mb-0">正在同步工具页...</p>
      ) : null}
      {!loading && tools.length === 0 ? (
        <p className="text-secondary mb-0">暂无已发布工具。</p>
      ) : null}
      {tools.map((tool) => (
        <article key={`${tool.title}-${tool.href}`} className="aito-tool-item">
          <div>
            <h3>{tool.title}</h3>
            <p>{tool.description}</p>
            <div>
              {tool.version ? <span>版本 {tool.version}</span> : null}
              {typeof tool.downloads === 'number' ? (
                <span>{tool.downloads} 次下载</span>
              ) : null}
            </div>
          </div>
          <a href={tool.href}>下载</a>
        </article>
      ))}
    </section>
  );
}

export const AitoForumRightRail = ({ loggedIn }: { loggedIn?: boolean }) => (
  <aside className="aito-right-rail" aria-label="AitoAPI forum sidebar">
    <AitoForumAd />
    <AitoForumToolFeed compact />
    <section className="aito-side-card">
      <strong>更多工具关注：</strong>
      <a href="/tool/">aitoapi.cloud/tool</a>
    </section>
    {!loggedIn ? (
      <section className="aito-side-card compact">
        <span>登录后可回答、评论和投票。</span>
        <a href={newApiLogin}>登录账号</a>
      </section>
    ) : null}
  </aside>
);
